import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React from "react";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import { onHasNotch } from "../../../utils/DeviceUtils";
import {
  CalendarCheckLightIcon,
  CalendarSearchLightIcon,
  ChatCircleTextLightIcon,
  DollarBillLineIcon,
  UsersIcon,
} from "../../../utils/icones";
import { MainTab } from "../MainV2Screen";

type Props = {
  activeTab: MainTab;
  totalNewChats: number;
  onClickTab: (tab: MainTab) => void;
};

export default function MainFooter(props: Props) {
  const items: {
    id: MainTab;
    label: string;
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    badge?: string | number;
  }[] = [
    {
      id: "explore",
      label: translate(STRINGS.EXPLORE),
      icon: CalendarSearchLightIcon,
    },
    {
      id: "my-visits",
      label: translate(STRINGS.MY_VISITS),
      icon: CalendarCheckLightIcon,
    },
    {
      id: "instapay",
      label: translate(STRINGS.INSTAPAY),
      icon: DollarBillLineIcon,
    },
    {
      id: "patients",
      label: translate(STRINGS.PATIENTS),
      icon: UsersIcon,
    },
    {
      id: "chat",
      label: translate(STRINGS.CHAT),
      icon: ChatCircleTextLightIcon,
      badge: props.totalNewChats,
    },
  ];

  return (
    <Root>
      {items.map((item) => (
        <Item
          key={item.id}
          label={item.label}
          icon={item.icon}
          badge={item.badge}
          isActive={props.activeTab === item.id}
          onClick={() => props.onClickTab(item.id)}
        />
      ))}
    </Root>
  );
}

const Root = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: white;
  background: black;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.08);
  padding: 16px 24px 16px 24px;
  position: relative;
  z-index: 1;

  ${onHasNotch(
    () => css`
      padding-bottom: env(safe-area-inset-bottom);
    `
  )}
`;

function Item(props: {
  isActive: boolean;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  badge?: string | number;
  label: string;
  onClick: () => void;
}) {
  return (
    <ItemRoot onClick={props.onClick}>
      {props.badge !== undefined && props.badge !== 0 && <Badge>{props.badge}</Badge>}
      <Icon as={props.icon} data-active={props.isActive} />
      <Label data-active={props.isActive}>{props.label}</Label>
    </ItemRoot>
  );
}

const Badge = styled.div`
  --size: 16px;
  position: absolute;
  top: 0;
  right: 0;
  width: var(--size);
  height: var(--size);
  transform: translate(25%, -25%);
  background-color: var(--primary-color);
  border-radius: 50%;
  color: white;
  font-size: 10px;
  line-height: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2;
  position: relative;
`;

const Icon = styled.div<{ ["data-active"]: boolean }>`
  --size: 24px;
  width: var(--size);
  height: var(--size);
  color: white;

  &[data-active="true"] {
    color: var(--primary-color);
  }
`;

const Label = styled.div<{ ["data-active"]: boolean }>`
  color: white;
  
  font-size: 12px;
  line-height: 20px;
  font-weight: 600;

  &[data-active="true"] {
    color: var(--primary-color);
  }
`;

MainFooter.Root = Root;
